import { motion } from "framer-motion";
import { CalendarDays, Clock, Sparkles, Shirt, Drama } from "lucide-react";
import { ChapterHeading } from "@/components/ChapterHeading";

const cards = [
  {
    icon: CalendarDays,
    label: "The Date",
    value: "Saturday, 5 December 2026",
    note: "Save it. Guard it. Tell no one who mustn't know.",
    span: "md:col-span-7",
    testId: "plan-card-date",
  },
  {
    icon: Clock,
    label: "The Hour",
    value: "7:40pm sharp",
    note: "Arrive by 7:40pm and be completely hidden by 7:55pm — Lindsay walks in at 8:00pm. Carriages at 1:00am.",
    span: "md:col-span-5",
    testId: "plan-card-time",
  },
  {
    icon: Sparkles,
    label: "The Theme",
    value: "Phantasmagoria",
    note: "(noun: a confusing or strange series of images; a weird mix of sights, or a fever dream where things feel unreal).",
    noteClass: "italic text-xs",
    note2: "Black and gold set the scene, woven through with Lindsay's great loves — music, theatre, dance, literature and sci-fi & fantasy. One rule above all: arrive masked. A simple eye mask or an elaborate Venetian creation — costume and elegance, certainly, but mystery above all.",
    span: "md:col-span-5",
    testId: "plan-card-theme",
  },
  {
    icon: Shirt,
    label: "Dress Code",
    value: "Come as a character",
    note: "Lindsay's passions unite the evening — so it would be fantastic if as many people as possible really threw themselves into it, let their imaginations run wild and came dressed as a character from the worlds she loves.",
    note2: "As this is a masked event, where possible, let your mask match your character — sheet music or an instrument motif for music, a book-page mask for literature, something dramatic for theatre, and so on.",
    note3: "And if expressing yourself isn't your thing, a simple eye mask with a dinner jacket or evening gown will be just fine… all in a black and gold theme, of course.",
    span: "md:col-span-12",
    wide: true,
    testId: "plan-card-dress",
  },
  {
    icon: Drama,
    label: "Fancy Dress",
    value: "Examples",
    note: "These are just sparks, not limits — the elegance of the black and gold backdrop fused with a glorious mishmash of styles and characters.",
    groups: [
      { cat: "Literature", names: "Charles Dickens · Jane Austen · William Shakespeare · Agatha Christie" },
      { cat: "Sci-fi & Fantasy", names: "Pa'u Zotoh Zhaan · Dr Who · Trinity · a Guardian of the Galaxy · Barbarella" },
      { cat: "Dance", names: "Darcey Bussell · Mikhail Baryshnikov · Fred Astaire · Eleanor Powell" },
      { cat: "Theatre", names: "The Great Gatsby · Fantine · Sweeney Todd · the Emcee" },
      { cat: "Music", names: "Adam Ant · Billie Eilish · Cat Stevens · Tina Turner · Amy Winehouse" },
    ],
    span: "md:col-span-12",
    wide: true,
    testId: "plan-card-examples",
  },
];

export const PlanChapter = () => (
  <section className="px-6 py-24 md:px-16 md:py-36 lg:px-24" data-testid="chapter-plan">
    <ChapterHeading number="01" title="The" italic="Plan" testId="chapter-plan-heading" />
    <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
      {cards.map((card, i) => (
        <motion.div
          key={card.testId}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
          whileHover={{ y: -4 }}
          className={`group border border-white/10 bg-[#0D0D0D] p-10 transition-colors duration-300 hover:border-gold/30 md:p-12 ${card.span}`}
          data-testid={card.testId}
        >
          <card.icon className="mb-8 h-5 w-5 text-gold transition-transform duration-300 group-hover:-translate-y-1" strokeWidth={1.25} />
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#666666]">{card.label}</p>
          <p className="font-display text-3xl font-light text-[#F5F5F0] md:text-4xl">{card.value}</p>
          {card.note && (
            <p className={`mt-5 text-sm leading-relaxed text-[#A1A19A] ${card.wide ? "max-w-3xl" : "max-w-sm"} ${card.noteClass || ""}`}>{card.note}</p>
          )}
          {card.note2 && (
            <p className={`mt-4 text-sm leading-relaxed text-[#A1A19A] ${card.wide ? "max-w-3xl" : "max-w-sm"}`}>{card.note2}</p>
          )}
          {card.note3 && (
            <p className={`mt-4 text-sm leading-relaxed text-[#A1A19A] ${card.wide ? "max-w-3xl" : "max-w-sm"}`}>{card.note3}</p>
          )}
          {card.groups && (
            <dl className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {card.groups.map((g) => (
                <div key={g.cat}>
                  <dt className="text-xs uppercase tracking-[0.25em] text-gold">{g.cat}</dt>
                  <dd className="mt-2 text-sm leading-relaxed text-[#A1A19A]">{g.names}</dd>
                </div>
              ))}
            </dl>
          )}
        </motion.div>
      ))}
    </div>
  </section>
);
